import { useEffect, useState, useCallback, useRef } from 'react';
import { useSetLoading } from './useSetLoading';

import type { Awaitable } from '@/types/general';
import type { Dispatch, SetStateAction } from 'react';

export type UseFetchProps<FetchedData, Input = unknown, OutputData = FetchedData> = {
    /**
     * A function that will fetch/compute some data to be funnelled
     * into the optional `modifier` function.
     *
     * **Tip:** Return `undefined` from the fetcher function to exit early
     * from the workflow and not modify the state.
     */
    fetcher: (input?: Input) => Awaitable<FetchedData | undefined>;
    /**
     * The value to be used as the initial state. Can be either empty
     * state or dummy values.
     */
    defaultValue: OutputData;
    /**
     * An optional modifier function that accepts the result of the
     * fetcher function and returns a modified version.
     */
    modifier?: (data: FetchedData) => OutputData | undefined;
    errorHandler?: (err: any) => Awaitable<any>;
    /**
     * When set to `true`, `useFetch` will **not** run the fetching
     * function on the first render of the component. The `refetch()`
     * function must be used to load in order to run the first fetch.
     */
    lazy?: boolean;
    /**
     * Whether or not to automatically display the loading spinner screen.
     */
    spinner?: boolean;
};

export type UseFetchOutput<Output, Input = unknown> = {
    /**
     * Whether or not the data is currently loading.
     *
     * **NOTE:** Don't use for loading spinner conditionals.
     */
    loading: boolean;
    /**
     * Will not be `null` if any error has transpired.
     */
    error: any | null;
    data: Output;
    refetch: (input?: Input) => void;
    /**
     * Modify the hook's internal state. Doesn't run
     * the `refetch` function.
     */
    setData: Dispatch<SetStateAction<Output>>;
};

/**
 * Provide no `modifier` in order to directly set the state based on the resolved
 * value of the `fetcher` function.
 *
 * With this usage, the `defaultValue` must match the `fetcher`'s return type.
 */
export function useFetch<Fetched, Input>(
    data: UseFetchProps<Fetched, Input, Fetched> & { modifier?: never }
): UseFetchOutput<Fetched, Input>;
/**
 * Provide a `modifier` to mutate the fetched data after fetching.
 *
 * With this usage, the `defaultValue` must match the `modifier`'s return type.
 */
export function useFetch<Fetched, Input, Output>(
    data: UseFetchProps<Fetched, Input, Output> & { modifier: (data: Fetched) => Output }
): UseFetchOutput<Output, Input>;
/**
 * Fetch & modify data, and manage its state.
 *
 * **Note:** Uses {@link useSetLoading} under the hood. So the loading screen will
 * automatically appear while the request is not yet complete. To disable this
 * functionality, pass in `true` for the `spinner` option.
 */
export function useFetch<Fetched, Input = unknown, Output = Fetched>({
    fetcher,
    modifier,
    defaultValue,
    errorHandler,
    lazy = false,
    spinner = true,
}: UseFetchProps<Fetched, Input, Output>): UseFetchOutput<Output, Input> {
    const { loading, setLoading } = useSetLoading(spinner && !lazy);
    const [standardLoading, setStandardLoading] = useState(!spinner && !lazy);
    const [data, setData] = useState(defaultValue);
    const [error, setError] = useState<any | null>(null);
    const isFetching = useRef<boolean>(false);

    const setIsLoading = spinner ? setLoading : setStandardLoading;

    const fetchData = useCallback(
        async (input?: Input) => {
            // Don't allow multiple fetches to happen at once.
            // Avoids race conditions.
            if (isFetching.current) return;
            isFetching.current = true;

            setError(null);
            setIsLoading(true);

            try {
                const data = await fetcher(input);
                if (data === undefined) return;
                const modified = (modifier ? modifier(data) : data) as Output | undefined;
                if (modified === undefined) return;
                setData(modified);
            } catch (error) {
                setError(error);
                errorHandler?.(error);
                // eslint-disable-next-line no-console
                console.error('Error occurred in useFetch:', error);
            } finally {
                isFetching.current = false;
                setIsLoading(false);
            }
        },
        [fetcher, modifier, errorHandler]
    );

    // Fetch the data on first render
    useEffect(() => {
        if (lazy) return;
        fetchData();
    }, []);

    return {
        loading: spinner ? loading : standardLoading,
        error,
        data,
        refetch: (input?: Input) => void fetchData(input),
        setData,
    };
}
